// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Team & access control  (client for /admin/team — who has staff access, at what role)
// Admin-only endpoints (narrower than the usual admin/editor write gate: only a Super Admin can see or change who else has access). The three roles are the SAME ones every other admin
// endpoint already checks (admin/editor/viewer) — there is no separate, finer-grained permission system. GET /admin/team lists real Firebase Auth accounts with a role claim (username, role,
// enabled/disabled, real last sign-in, whether they've enrolled a fingerprint); POST creates one; PUT changes a role or enables/disables an account; GET /admin/team/audit is the real log of
// who did which of those three things, through this screen, and when.
// Neighbours: pages/admin/AdminTeam, backend api/team, services/team, domain/staff_account, integrations/identity.
// ─────────────────────────────────────────────────────────────────────────────
import { apiAuthed } from "./api";

export type StaffRole = "admin" | "editor" | "viewer";

export interface StaffAccount {
  uid: string;
  username: string;
  role: StaffRole;
  disabled: boolean;
  hasPasskey: boolean;
  isSelf: boolean;
  createdAt: string | null;
  lastSignInAt: string | null;
}

export interface AuditEntry {
  id: string;
  at: string;
  byUsername: string;
  action: "created" | "role_changed" | "enabled" | "disabled";
  targetUsername: string;
  detail: string;
}

export const fetchTeam = () => apiAuthed<StaffAccount[]>("GET", "/admin/team");
export const fetchTeamAudit = () => apiAuthed<AuditEntry[]>("GET", "/admin/team/audit");
export const createStaffAccount = (username: string, password: string, role: StaffRole) =>
  apiAuthed<StaffAccount>("POST", "/admin/team", { username, password, role });
export const setStaffRole = (uid: string, role: StaffRole) =>
  apiAuthed<StaffAccount>("PUT", `/admin/team/${encodeURIComponent(uid)}/role`, { role });
export const setStaffDisabled = (uid: string, disabled: boolean) =>
  apiAuthed<StaffAccount>("PUT", `/admin/team/${encodeURIComponent(uid)}/status`, { disabled });

export const ROLE_LABEL: Record<StaffRole, string> = { admin: "Admin", editor: "Editor", viewer: "Viewer" };
export const ROLE_HINT: Record<StaffRole, string> = {
  admin: "Everything, including managing other staff accounts here.",
  editor: "Can change orders, claims, requests and the other write screens.",
  viewer: "Read-only everywhere in the admin panel.",
};

const USERNAME_RE = /^[a-z0-9][a-z0-9._-]{2,31}$/;
export const MIN_PASSWORD = 10;

/** Mirrors backend domain/staff_account.py so staff see the problem before saving. */
export function usernameProblem(username: string): string | null {
  return USERNAME_RE.test(username) ? null : "A username is 3 to 32 characters: lowercase letters, digits, dot, dash or underscore.";
}
export function passwordProblem(password: string): string | null {
  return password.length >= MIN_PASSWORD ? null : `The password must be at least ${MIN_PASSWORD} characters.`;
}

export const ACTION_LABEL: Record<AuditEntry["action"], string> = {
  created: "created",
  role_changed: "changed the role of",
  enabled: "enabled",
  disabled: "disabled",
};
